import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MobileContainer, Card, Button, Input, Label, FormGroup, ErrorMessage, Logo, SuccessMessage } from '../theme/GlobalStyles';
import { FaArrowLeft, FaMapMarkerAlt, FaCheck, FaSpinner } from 'react-icons/fa';
import CustomAlert from '../components/CustomAlert';
import cdePlusLogo from '../assets/images/cde_plus_logo.png';
import { useDependentDropdowns, DropdownOption } from '../hooks/useDependentDropdowns';
import SearchableSelect from '../components/SearchableSelect';

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem 0;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.primary};
  padding: 0.5rem;
  margin-right: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(244, 0, 0, 0.1);
    border-radius: 50%;
  }
`;

const HeaderContent = styled.div`
  text-align: center;
  flex: 1;
`;

const Title = styled.h1`
  color: ${props => props.theme.primary};
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
`;

const RequiredMark = styled.span`
  color: #f44336;
  margin-left: 2px;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.inputBorder};
  border-radius: 8px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    border-color: ${props => props.theme.inputFocus};
    outline: none;
  }
`;


const LocationButton = styled(Button)`
  background: ${props => props.theme.success};
  margin-bottom: 1rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.successDark};
  }
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.primary};
  font-size: 1.1rem;
`;

const RegistrationCard = styled(Card)`
  margin-bottom: 1rem;
`;

interface RegistrationData {
  greenTagNumber: string;
  cdeType: string;
  ownerName: string;
  contactNumber: string;
  storeOutletName: string;
  address: string;
  province: string;
  city: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  email: string;
}

interface CDEType {
  id: string;
  name: string;
  description: string;
}

interface ValidationState {
  isValidating: boolean;
  isValid?: boolean;
  message?: string;
  formattedValue?: string;
}

const RegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegistrationData>({
    greenTagNumber: '',
    cdeType: '',
    ownerName: '',
    contactNumber: '',
    storeOutletName: '',
    address: '',
    province: '',
    city: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    email: ''
  });

  const [errors, setErrors] = useState<Partial<RegistrationData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [cdeTypes, setCdeTypes] = useState<CDEType[]>([]);
  const [validationStates, setValidationStates] = useState<{
    greenTag: ValidationState;
    phone: ValidationState;
  }>({
    greenTag: { isValidating: false },
    phone: { isValidating: false }
  });

  // Configure dependent dropdowns with useMemo to prevent recreation
  const dropdownConfigs = useMemo(() => [
    {
      fieldName: 'province',
      dataSource: '/Dropdown/GetListDataByModule?ModuleName=Province',
      dependencies: []
    },
    {
      fieldName: 'city',
      dataSource: '/Dropdown/GetListDataByModule?ModuleName=City_By_Parent&SearchText={province}',
      dependencies: ['province']
    },
    {
      fieldName: 'postalCode',
      dataSource: '/Dropdown/GetListDataByModule?ModuleName=PostalCode_By_Parent&SearchText={city}',
      dependencies: ['city']
    }
  ], []); // Empty dependency array since these configs don't change

  // Alert state management
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message: string;
  }>({
    isVisible: false,
    type: 'info',
    message: ''
  });

  // Helper function to show custom alerts
  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string, title?: string) => {
    setAlert({
      isVisible: true,
      type,
      message,
      title
    });
  };

  const handleAlertClose = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    // Get Green Tag number from localStorage if available
    const storedGreenTag = localStorage.getItem('greenTagNumber');
    if (storedGreenTag) {
      setFormData(prev => ({ ...prev, greenTagNumber: storedGreenTag }));
    }
    
    // Load customer data from localStorage if available
    const storedCustomerData = localStorage.getItem('customerData');
    if (storedCustomerData) {
      try {
        const customerData = JSON.parse(storedCustomerData);
        setFormData(prev => ({
          ...prev,
          ownerName: customerData.name || customerData.Name || '',
          storeOutletName: customerData.aliasName || customerData.AliasName || '',
          address: customerData.address || customerData.Address || customerData.Line1 || '',
          city: customerData.city || customerData.City || '',
          province: customerData.province || customerData.Province || customerData.RegionName || '',
          postalCode: customerData.zipCode || customerData.ZipCode || customerData.postalCode || '',
          contactNumber: customerData.mobile || customerData.Mobile || customerData.phone || customerData.Phone || '',
          cdeType: customerData.type || customerData.Type || ''
        }));
      } catch (error) {
        console.error('Error parsing customer data:', error);
      }
    }
    
    // Load CDE types
    loadCDETypes();
  }, []);

  const loadCDETypes = async () => {
    try {
      const apiService = await import('../services/apiService');
      const response = await apiService.default.getCDETypes();
      if (response.success) {
        // Filter to only show Fountain and Cooler
        const filteredTypes = response.data.filter((type: CDEType) => 
          type.name === 'Fountain' || type.name === 'Cooler'
        );
        setCdeTypes(filteredTypes);
      }
    } catch (error) {
      console.error('Failed to load CDE types:', error);
      // No fallback data - form will show error if CDE types can't be loaded
    }
  };

  // Use the dependent dropdowns hook
  const { dropdownOptions, loadingFields, clearDependentFields } = useDependentDropdowns(
    dropdownConfigs,
    formData,
    (fieldName: string, value: any) => {
      // Direct state update for dependent dropdowns to avoid circular calls
      setFormData(prev => ({ ...prev, [fieldName]: value }));
      
      // Clear error when value changes
      if (errors[fieldName as keyof RegistrationData]) {
        setErrors(prev => ({ ...prev, [fieldName]: '' }));
      }
    }
  );

  const handleInputChange = (field: keyof RegistrationData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear dependent fields when parent field changes (only for province and city)
    if (field === 'province' || field === 'city') {
      clearDependentFields(field);
    }
    
    if (field === 'email') {
      validateEmail(value);
    }
  };
const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email) {
    setErrors(prev => ({ ...prev, email: 'Email is required' }));
  } else if (!emailRegex.test(email)) {
    setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
  } else {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.email;
      return newErrors;
    });
  }
};

  const validateGreenTagOnBlur = async (greenTagNumber: string) => {
    if (!greenTagNumber.trim()) return;
    
    setValidationStates(prev => ({
      ...prev,
      greenTag: { isValidating: true }
    }));

    try {
      const apiService = await import('../services/apiService');
      const response = await apiService.default.validateCustomer(greenTagNumber);
      
      if (response.success && response.exists) {
        setValidationStates(prev => ({
          ...prev,
          greenTag: { 
            isValidating: false, 
            isValid: false, 
            message: 'This Green Tag is already registered' 
          }
        }));
        setErrors(prev => ({ ...prev, greenTagNumber: 'This Green Tag is already registered' }));
      } else {
        setValidationStates(prev => ({
          ...prev,
          greenTag: { 
            isValidating: false, 
            isValid: true, 
            message: 'Green Tag is available' 
          }
        }));
      }
    } catch (error) {
      setValidationStates(prev => ({
        ...prev,
        greenTag: { 
          isValidating: false, 
          isValid: undefined, 
          message: 'Unable to validate at this time' 
        }
      }));
    }
  };

  const validatePhoneOnBlur = async (phoneNumber: string) => {
    if (!phoneNumber.trim()) return;
    
    setValidationStates(prev => ({
      ...prev,
      phone: { isValidating: true }
    }));

    try {
      const apiService = await import('../services/apiService');
      const response = await apiService.default.validatePhone(phoneNumber);
      
      if (response.success && response.isValid) {
        setValidationStates(prev => ({
          ...prev,
          phone: { 
            isValidating: false, 
            isValid: true, 
            message: `Valid ${response.phoneType} (${response.countryInfo})`,
            formattedValue: response.formattedPhone
          }
        }));
        // Auto-update with formatted phone
        setFormData(prev => ({ ...prev, contactNumber: response.formattedPhone }));
      } else {
        setValidationStates(prev => ({
          ...prev,
          phone: { 
            isValidating: false, 
            isValid: false, 
            message: 'Please enter a valid international phone number' 
          }
        }));
        setErrors(prev => ({ ...prev, contactNumber: 'Please enter a valid international phone number' }));
      }
    } catch (error) {
      setValidationStates(prev => ({
        ...prev,
        phone: { 
          isValidating: false, 
          isValid: undefined, 
          message: 'Unable to validate at this time' 
        }
      }));
    }
  };

  const getCurrentLocation = () => {
    setLocationStatus('loading');
    
    if (!navigator.geolocation) {
      setLocationStatus('error');
      showAlert('error', 'Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          latitude: latitude.toString(),
          longitude: longitude.toString()
        }));
        setLocationStatus('success');
      },
      (error) => {
        setLocationStatus('error');
        console.error('Error getting location:', error);
        showAlert('warning', 'Error getting location. Please enter manually if needed.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<RegistrationData> = {};

    // Required field validation
    if (!formData.greenTagNumber.trim()) newErrors.greenTagNumber = 'Green Tag Number is required';
    if (!formData.cdeType) newErrors.cdeType = 'CDE Type is required';
    if (!formData.ownerName.trim()) newErrors.ownerName = 'Owner Name is required';
    if (!formData.contactNumber.trim()) newErrors.contactNumber = 'Contact Number is required';
    if (!formData.storeOutletName.trim()) newErrors.storeOutletName = 'Store/Outlet Name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.province.trim()) newErrors.province = 'Province is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal Code is required';
    if (!formData.latitude.trim()) newErrors.latitude = 'Latitude is required';
    if (!formData.longitude.trim()) newErrors.longitude = 'Longitude is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    // Enhanced phone number validation - server-side validation is preferred
    if (formData.contactNumber && validationStates.phone.isValid === false) {
      newErrors.contactNumber = validationStates.phone.message || 'Please enter a valid phone number';
    }

    // Green tag validation
    if (formData.greenTagNumber && validationStates.greenTag.isValid === false) {
      newErrors.greenTagNumber = validationStates.greenTag.message || 'Green Tag validation failed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Call registration API
      const apiService = await import('../services/apiService');
      const response = await apiService.default.registerCustomer({
        greenTagNumber: formData.greenTagNumber,
        cdeType: formData.cdeType,
        ownerName: formData.ownerName,
        contactNumber: formData.contactNumber,
        storeOutletName: formData.storeOutletName,
        address: formData.address,
        province: formData.province,
        city: formData.city,
        postalCode: formData.postalCode,
        latitude: formData.latitude,
        longitude: formData.longitude,
        email: formData.email
      });

      if (response.success) {
        showAlert('success', `Registration submitted successfully! Your request is ${response.status || 'pending approval'}.`, 'Success');
        
        // Clear localStorage and navigate back after a short delay
        setTimeout(() => {
          localStorage.removeItem('greenTagNumber');
          navigate('/');
        }, 2000);
      } else {
        showAlert('error', response.message || 'Registration failed. Please try again.', 'Registration Failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      showAlert('error', error.message || 'Error submitting registration. Please try again.', 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLocationButtonText = () => {
    switch (locationStatus) {
      case 'loading': return 'Getting Location...';
      case 'success': return 'Location Captured';
      case 'error': return 'Retry Location';
      default: return 'Capture Current Location';
    }
  };

  const getLocationButtonIcon = () => {
    switch (locationStatus) {
      case 'success': return React.createElement(FaCheck as any, {});
      default: return React.createElement(FaMapMarkerAlt as any, {});
    }
  };

  // Render dependent dropdown component
  const renderDependentDropdown = (
    fieldName: keyof RegistrationData,
    label: string,
    placeholder: string,
    required: boolean = true
  ) => {
    const options = dropdownOptions[fieldName] || [];
    const isLoading = loadingFields.has(fieldName);
    const hasError = !!errors[fieldName];
    const value = formData[fieldName] || '';

    return (
      <FormGroup>
        <Label htmlFor={fieldName}>
          {label} {required && <RequiredMark>*</RequiredMark>}
        </Label>
        <SearchableSelect
          value={value}
          onChange={(newValue) => handleInputChange(fieldName, newValue)}
          options={options}
          placeholder={isLoading ? `Loading ${label.toLowerCase()}...` : placeholder}
          disabled={isLoading}
          hasError={hasError}
          required={required}
        />
        {errors[fieldName] && <ErrorMessage>{errors[fieldName]}</ErrorMessage>}
      </FormGroup>
    );
  };

  return (
    <MobileContainer>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          {React.createElement(FaArrowLeft as any, { size: 20 })}
        </BackButton>
        <HeaderContent>
          <Logo src={cdePlusLogo} alt="CDE+ Service Portal" style={{ height: '40px' }} />
          <Title>CDE Registration</Title>
          <Subtitle>Registration</Subtitle>
        </HeaderContent>
      </Header>

      <RegistrationCard>
        <SectionTitle>Green Tag Information</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="greenTag">Green tag Number *</Label>
          <div style={{ position: 'relative' }}>
            <Input
              id="greenTag"
              type="text"
              placeholder="Enter Green Tag Number"
              value={formData.greenTagNumber}
              onChange={(e) => handleInputChange('greenTagNumber', e.target.value)}
              onBlur={(e) => validateGreenTagOnBlur(e.target.value)}
              style={{
                borderColor: validationStates.greenTag.isValid === true ? '#4CAF50' : 
                           validationStates.greenTag.isValid === false ? '#f44336' : undefined
              }}
            />
            {validationStates.greenTag.isValidating && (
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                {React.createElement(FaSpinner as any, { className: 'fa-spin', color: '#666' })}
              </div>
            )}
          </div>
          {errors.greenTagNumber && <ErrorMessage>{errors.greenTagNumber}</ErrorMessage>}
          {validationStates.greenTag.isValid === true && validationStates.greenTag.message && (
            <SuccessMessage>{validationStates.greenTag.message}</SuccessMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="cdeType">CDE Type *</Label>
          <Select
            id="cdeType"
            value={formData.cdeType}
            onChange={(e) => handleInputChange('cdeType', e.target.value)}
          >
            <option value="">Select CDE Type</option>
            <option value="Fountain">Fountain</option>
            <option value="Cooler">Cooler</option>
          </Select>
          {errors.cdeType && <ErrorMessage>{errors.cdeType}</ErrorMessage>}
        </FormGroup>
      </RegistrationCard>

      <RegistrationCard>
        <SectionTitle>Owner Details</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="ownerName">Owner Name *</Label>
          <Input
            id="ownerName"
            type="text"
            placeholder="Enter Owner Name"
            value={formData.ownerName}
            onChange={(e) => handleInputChange('ownerName', e.target.value)}
          />
          {errors.ownerName && <ErrorMessage>{errors.ownerName}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="contactNumber">Contact Number *</Label>
          <div style={{ position: 'relative' }}>
            <Input
              id="contactNumber"
              type="tel"
              placeholder="+1 555 123 4567 or 1234567890"
              value={formData.contactNumber}
              onChange={(e) => handleInputChange('contactNumber', e.target.value)}
              onBlur={(e) => validatePhoneOnBlur(e.target.value)}
              style={{
                borderColor: validationStates.phone.isValid === true ? '#4CAF50' : 
                           validationStates.phone.isValid === false ? '#f44336' : undefined
              }}
            />
            {validationStates.phone.isValidating && (
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                {React.createElement(FaSpinner as any, { className: 'fa-spin', color: '#666' })}
              </div>
            )}
          </div>
          {errors.contactNumber && <ErrorMessage>{errors.contactNumber}</ErrorMessage>}
          {validationStates.phone.isValid === true && validationStates.phone.message && (
            <SuccessMessage>{validationStates.phone.message}</SuccessMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="storeOutletName">Store/Outlet Name *</Label>
          <Input
            id="storeOutletName"
            type="text"
            placeholder="Enter Store Name"
            value={formData.storeOutletName}
            onChange={(e) => handleInputChange('storeOutletName', e.target.value)}
          />
          {errors.storeOutletName && <ErrorMessage>{errors.storeOutletName}</ErrorMessage>}
        </FormGroup>
      </RegistrationCard>

      <RegistrationCard>
        <SectionTitle>Outlet Details</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="address">Address *</Label>
          <Input
            id="address"
            type="text"
            placeholder="Complete Address"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
          />
          {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
        </FormGroup>

        {renderDependentDropdown('province', 'Province', 'Select Province')}
        {renderDependentDropdown('city', 'City', 'Select City')}
        {renderDependentDropdown('postalCode', 'Postal Code', 'Select Postal Code')}

        <LocationButton 
          onClick={getCurrentLocation} 
          disabled={locationStatus === 'loading'}
        >
          {getLocationButtonIcon()}
          {getLocationButtonText()}
        </LocationButton>

        <FormGroup>
          <Label htmlFor="latitude">GeoCode (Latitude) *</Label>
          <Input
            id="latitude"
            type="text"
            placeholder="14.640531"
            value={formData.latitude}
            onChange={(e) => handleInputChange('latitude', e.target.value)}
          />
          {errors.latitude && <ErrorMessage>{errors.latitude}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="longitude">GeoCode (Longitude) *</Label>
          <Input
            id="longitude"
            type="text"
            placeholder="121.045649"
            value={formData.longitude}
            onChange={(e) => handleInputChange('longitude', e.target.value)}
          />
          {errors.longitude && <ErrorMessage>{errors.longitude}</ErrorMessage>}
        </FormGroup>
         <FormGroup>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="text"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)} 
            onBlur={(e) => validateEmail(e.target.value)}  
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>
      </RegistrationCard>

      <Button 
        onClick={handleSubmit} 
        disabled={isSubmitting}
        style={{ marginBottom: '2rem' }}
      >
        {isSubmitting ? 'Registering...' : 'Register CDE'}
      </Button>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={handleAlertClose}
      />
    </MobileContainer>
  );
};

export default RegistrationPage;